import { getPowerhiveBackendUrl } from './powerhiveApi';

function getSolarSitesApiUrl(): string {
  return `${getPowerhiveBackendUrl()}/api/v1/powerhive/solar/sites`;
}

export const SOLAR_SITES_API = getSolarSitesApiUrl();



export interface SolarPanelSpecs {
  panelModel: string | null;
  estimatedPanels: number | null;
}

export interface SolarSite {
  id: string;
  name: string;
  type: string | null;
  lat: number;
  lng: number;
  location: string | null;
  status: string;
  planned: boolean;
  currentPowerKw: number | null;
  energyTodayKwh: number | null;
  capacityKwp: number | null;
  meterReadingAt: string | null;
  telemetryStatus: string | null;
  locationSource: string | null;
  coordinateSampleCount: number | null;
  consumptionMeterCount: number | null;
  activeConsumptionMeterCount: number | null;
  latestConsumptionReadingAt: string | null;
  panelSpecs: SolarPanelSpecs;
}

function finite(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

export function normalizeSolarSite(raw: unknown): SolarSite | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const position =
    row.position && typeof row.position === 'object'
      ? (row.position as Record<string, unknown>)
      : {};
  const panel =
    row.panel_specs && typeof row.panel_specs === 'object'
      ? (row.panel_specs as Record<string, unknown>)
      : {};

  const id = text(row.site_id);
  const name = text(row.name) ?? id;
  const lat = finite(row.latitude ?? position.latitude);
  const lng = finite(row.longitude ?? position.longitude);
  if (!id || !name || lat === null || lng === null) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180 || (lat === 0 && lng === 0)) return null;

  const locationSource = text(row.location_source);
  const status = text(row.status) ?? (locationSource ? 'Co-located' : 'Unknown');
  const currentPowerKw = finite(row.current_power_kw ?? row.power_now_kw);
  const energyTodayKwh = finite(row.energy_today_kwh);
  const capacityKwp = finite(row.capacity_kwp);
  const estimatedPanels = finite(panel.estimated_panels);

  return {
    id,
    name,
    type: text(row.type),
    lat,
    lng,
    location: text(row.location ?? position.location),
    status,
    planned: status.toLowerCase() === 'planned',
    currentPowerKw: currentPowerKw === null ? null : Math.max(0, currentPowerKw),
    energyTodayKwh: energyTodayKwh === null ? null : Math.max(0, energyTodayKwh),
    capacityKwp: capacityKwp === null ? null : Math.max(0, capacityKwp),
    meterReadingAt: text(row.meter_reading_at),
    telemetryStatus: text(row.telemetry_status),
    locationSource,
    coordinateSampleCount: finite(row.coordinate_sample_count),
    consumptionMeterCount: finite(row.consumption_meter_count),
    activeConsumptionMeterCount: finite(row.active_consumption_meter_count),
    latestConsumptionReadingAt: text(row.latest_consumption_reading_at),
    panelSpecs: {
      panelModel: text(panel.panel_model),
      estimatedPanels: estimatedPanels === null ? null : Math.max(0, Math.round(estimatedPanels)),
    },
  };
}

export function normalizeSolarPayload(payload: unknown): {
  sites: SolarSite[];
  rejected: number;
  reason: string | null;
} {
  const rows = Array.isArray(payload)
    ? payload
    : payload && typeof payload === 'object' && Array.isArray((payload as { data?: unknown }).data)
      ? (payload as { data: unknown[] }).data
      : [];
  const meta =
    payload &&
    typeof payload === 'object' &&
    (payload as { meta?: unknown }).meta &&
    typeof (payload as { meta: unknown }).meta === 'object'
      ? (payload as { meta: { reason?: unknown } }).meta
      : null;
  const byId = new Map<string, SolarSite>();
  let rejected = 0;
  for (const row of rows) {
    const site = normalizeSolarSite(row);
    if (!site) {
      rejected++;
      continue;
    }
    byId.set(site.id, site);
  }
  return { sites: [...byId.values()], rejected, reason: text(meta?.reason) };
}

export function solarUtilization(site: SolarSite): number | null {
  if (site.currentPowerKw === null || site.capacityKwp === null || site.capacityKwp <= 0)
    return null;
  return Math.max(0, (site.currentPowerKw / site.capacityKwp) * 100);
}
